import type { RouteContext } from '@qse/realtime'
import { useLocation, useParams } from 'react-router-dom'

import { AppRoutesList } from '@/router/routes'

/**
 * Hook to get current route context using React Router and AppRoutesList
 */
export function useRouteContext(): RouteContext {
  const location = useLocation()
  const params = useParams()

  const pathname = location.pathname
  const searchParams = new URLSearchParams(location.search)

  // Extract search filters from URL
  const search = searchParams.get('query') || ''
  const fileType = searchParams.get('fileType') || ''
  const isDir = searchParams.get('isDir') || ''
  const createdFrom = searchParams.get('createdFrom') || ''
  const createdTo = searchParams.get('createdTo') || ''

  // Helper to match base route (for dynamic routes)
  const matchBase = (route: string) => {
    // Remove trailing "/:id" or similar
    return route.replace(/:\w+$/, '')
  }

  if (pathname.startsWith(AppRoutesList.trash)) {
    return {
      route: 'trash',
      location: 'TRASH' as const,
      filters: {
        search: search || undefined,
        fileType: fileType || undefined,
        isDir: isDir === 'true' ? true : undefined,
        createdFrom: createdFrom || undefined,
        createdTo: createdTo || undefined,
      },
    }
  }

  if (pathname.startsWith(AppRoutesList.favorites)) {
    return {
      route: 'favorites',
      location: 'FAVORITE' as const,
      filters: {
        search: search || undefined,
        fileType: fileType || undefined,
        isDir: isDir === 'true' ? true : undefined,
        createdFrom: createdFrom || undefined,
        createdTo: createdTo || undefined,
      },
    }
  }

  if (pathname.startsWith(AppRoutesList.searchResults)) {
    return {
      route: 'search-results',
      filters: {
        search: search || undefined,
        fileType: fileType || undefined,
        isDir: isDir === 'true' ? true : undefined,
        createdFrom: createdFrom || undefined,
        createdTo: createdTo || undefined,
      },
    }
  }

  if (pathname.startsWith(matchBase(AppRoutesList.folder))) {
    // Extract folder ID from URL params or pathname
    const folderId = params.id || pathname.split(matchBase(AppRoutesList.folder))[1]?.split('?')[0]

    return {
      route: 'folder',
      parentId: folderId,
      filters: {
        search: search || undefined,
        fileType: fileType || undefined,
        isDir: isDir === 'true' ? true : undefined,
        createdFrom: createdFrom || undefined,
        createdTo: createdTo || undefined,
      },
    }
  }

  // Default dashboard route
  if (pathname.startsWith(AppRoutesList.dashboard)) {
    return {
      route: 'dashboard',
      location: 'HOME' as const,
      filters: {
        search: search || undefined,
        fileType: fileType || undefined,
        isDir: isDir === 'true' ? true : undefined,
        createdFrom: createdFrom || undefined,
        createdTo: createdTo || undefined,
      },
    }
  }

  // Fallback
  return {
    route: 'unknown',
    filters: {},
  }
}
